import { useEffect, useState } from "react";
import { UsersFetchAPI } from "../../../api/Users";
import { EventsFetchAPI } from "../../../api/Events";
import { MachinesFetchAPI } from "../../../api/Machines";
import { PaymentMethodFetchAPI } from "../../../api/PaymentMethod";
import { I, QUICK, getToken } from "../ReportsPanel";

const CASHIER_ROLE_IDS = [4, 21];
const WAITER_ROLE_ID = 15;

// Funcion pura (fuera del componente) para que useEffect no la vea como una dependencia
// nueva en cada render.
const hasFilterType = (filterDefs, type) => filterDefs.some((f) => f.type === type);

export function getUserBranchesWithIds() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return (user?.branchoffices || [])
      .map((b) => ({
        code: b.code !== undefined ? String(b.code) : "",
        id: b.branchoffice_id || b.id,
        name: b.branchoffice_name || b.name || String(b.code || ""),
      }))
      .filter((b) => b.code);
  } catch (_) {
    return [];
  }
}

export function getUserBusinessId() {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    return user?.business?.id || null;
  } catch (_) {
    return null;
  }
}

// Filtros declarados por catalog.filters[] de cada Plantilla SmartPay (fecha, sucursal, cajero,
// mesero, evento, maquina, metodo de pago) - reutiliza los mismos endpoints que ya usan los tabs
// legacy (UsersFetchAPI/EventsFetchAPI/MachinesFetchAPI/PaymentMethodFetchAPI), controlado
// completamente desde el padre (LegacyPreviewView) porque este necesita los valores actuales
// para llamar a /report-templates/:id/run.
export default function LegacyFilterBar({
  entry,
  dFrom, dTo, quickIndex, onDateChange,
  branchCodes, onBranchChange,
  cashierId, onCashierChange,
  waiterId, onWaiterChange,
  eventId, onEventChange,
  machineId, onMachineChange,
  paymentMethodId, onPaymentMethodChange,
}) {
  const filterDefs = entry?.filters || [];
  const has = (type) => hasFilterType(filterDefs, type);
  const branches = getUserBranchesWithIds();
  const businessId = getUserBusinessId();
  const singleBranch = branchCodes.length === 1 ? branches.find((b) => b.code === branchCodes[0]) : null;

  const [cashiers, setCashiers] = useState([]);
  const [waiters, setWaiters] = useState([]);
  const [events, setEvents] = useState([]);
  const [machines, setMachines] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    if (!has("cashier")) return;
    const token = getToken();
    (async () => {
      try {
        const res = singleBranch
          ? await UsersFetchAPI.getUsersByBranchofficesAndRole([singleBranch.id], CASHIER_ROLE_IDS, token)
          : await UsersFetchAPI.getUsersByBusinessAndRole(businessId, CASHIER_ROLE_IDS, token);
        setCashiers(res?.data?.users || []);
      } catch (_) {
        setCashiers([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleBranch?.id, businessId]);

  useEffect(() => {
    if (!has("waiter")) return;
    const token = getToken();
    (async () => {
      try {
        const res = singleBranch
          ? await UsersFetchAPI.getUsersByRoleAndBranchoffice(singleBranch.id, WAITER_ROLE_ID, token)
          : await UsersFetchAPI.getUsersByBusinessAndRole(businessId, [WAITER_ROLE_ID], token);
        setWaiters(res?.data?.users || []);
      } catch (_) {
        setWaiters([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleBranch?.id, businessId]);

  useEffect(() => {
    if (!has("event")) return;
    const token = getToken();
    (async () => {
      try {
        const res = singleBranch
          ? await EventsFetchAPI.getEventsByBranchoffice(singleBranch.id, token)
          : await EventsFetchAPI.getEventsByBusiness(businessId, token);
        setEvents(res?.data?.events || []);
      } catch (_) {
        setEvents([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleBranch?.id, businessId]);

  useEffect(() => {
    if (!has("machine") || !singleBranch) { setMachines([]); return; }
    const token = getToken();
    (async () => {
      try {
        const res = await MachinesFetchAPI.getMachinesByBranchOffice(singleBranch.id, token);
        setMachines(res?.data?.machines || res?.data || []);
      } catch (_) {
        setMachines([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [singleBranch, filterDefs]);

  useEffect(() => {
    if (!has("paymentMethod")) return;
    const token = getToken();
    (async () => {
      try {
        const res = await PaymentMethodFetchAPI.getPaymentMethodIntegrations(token);
        setPaymentMethods(res?.data?.integrations || res?.data || []);
      } catch (_) {
        setPaymentMethods([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleBranch = (code) => {
    if (has("branchMultiSelect")) {
      onBranchChange(branchCodes.includes(code) ? branchCodes.filter((c) => c !== code) : [...branchCodes, code]);
    } else {
      onBranchChange([code]);
    }
  };

  return (
    <div className="rb-filter-bar">
      <div className="rb-filter-bar__top">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div className="rb-filter-bar__icon"><I.Calendar /></div><span className="rb-filter-bar__title">Periodo</span></div>
        <div className="rb-filter-bar__quick">
          {QUICK.map((q, i) => (
            <button key={i} className={`rb-quick-btn ${quickIndex === i ? "rb-quick-btn--active" : ""}`} onClick={() => onDateChange(q.from(), q.to(), i)}>{q.label}</button>
          ))}
        </div>
      </div>
      <div className="rb-filter-bar__grid">
        <div className="rb-fg"><label className="rb-label">Desde</label><input type="date" className="rb-input" value={dFrom} onChange={(e) => onDateChange(e.target.value, dTo, null)} /></div>
        <div className="rb-fg"><label className="rb-label">Hasta</label><input type="date" className="rb-input" value={dTo} onChange={(e) => onDateChange(dFrom, e.target.value, null)} /></div>

        {(has("branchMultiSelect") || has("branchSelect")) && branches.length > 0 && (
          <div className="rb-fg">
            <label className="rb-label">Sucursal{has("branchMultiSelect") ? "(es)" : ""}</label>
            <details className="rb-branch-menu">
              <summary>{branchCodes.length === branches.length || branchCodes.length === 0 ? "Todas" : branchCodes.map((c) => branches.find((b) => b.code === c)?.name || c).join(", ")}</summary>
              <div className="rb-branch-panel">
                {has("branchMultiSelect") && <button type="button" className="rb-branch-all" onClick={() => onBranchChange(branches.map((b) => b.code))}>Todas</button>}
                {branches.map((branch) => (
                  has("branchMultiSelect") ? (
                    <label key={branch.code} className="rb-branch-option">
                      <input type="checkbox" checked={branchCodes.includes(branch.code)} onChange={() => toggleBranch(branch.code)} /><span>{branch.name}</span>
                    </label>
                  ) : (
                    <button key={branch.code} type="button" className="rb-branch-all" onClick={() => onBranchChange([branch.code])}>{branch.name}</button>
                  )
                ))}
              </div>
            </details>
          </div>
        )}

        {has("cashier") && (
          <div className="rb-fg"><label className="rb-label">Cajero</label>
            <select className="rb-input" value={cashierId || ""} onChange={(e) => onCashierChange(e.target.value || null)}>
              <option value="">Todos</option>
              {cashiers.map((u) => <option key={u.id} value={u.id}>{u.name} {u.lastName || ""}</option>)}
            </select>
          </div>
        )}

        {has("waiter") && (
          <div className="rb-fg"><label className="rb-label">Mesero</label>
            <select className="rb-input" value={waiterId || ""} onChange={(e) => onWaiterChange(e.target.value || null)}>
              <option value="">Todos</option>
              {waiters.map((u) => <option key={u.id} value={u.id}>{u.name} {u.lastName || ""}</option>)}
            </select>
          </div>
        )}

        {has("event") && (
          <div className="rb-fg"><label className="rb-label">Evento</label>
            <select className="rb-input" value={eventId || ""} onChange={(e) => onEventChange(e.target.value || null)}>
              <option value="">Todos</option>
              {events.map((ev) => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
            </select>
          </div>
        )}

        {has("machine") && (
          <div className="rb-fg"><label className="rb-label">Máquina</label>
            <select className="rb-input" value={machineId || ""} onChange={(e) => onMachineChange(e.target.value || null)} disabled={!singleBranch}>
              <option value="">Todas</option>
              {machines.map((m) => <option key={m.id} value={m.id}>{m.name || m.machine_name || m.id}</option>)}
            </select>
          </div>
        )}

        {has("paymentMethod") && (
          <div className="rb-fg"><label className="rb-label">Metodo de integración</label>
            <select className="rb-input" value={paymentMethodId || ""} onChange={(e) => onPaymentMethodChange(e.target.value || null)}>
              <option value="">Todos</option>
              {paymentMethods.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
      </div>
    </div>
  );
}
